import React, { Fragment, useState } from "react";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { showToast } from "../../../components/ToastifyNotification";
import Breadcrumb from "../../../components/common/breadcrumb";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { IpdRegistrationReportApi } from "../../../api/ipd/ipdReportsAPI";

const IpdRegistrationReport = () => {
    const [filteredData, setFilteredData] = useState([]);

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [search, setSearch] = useState("");

    const dispatch = useDispatch();

    const fetchData = async () => {
        dispatch({ type: "LOADER_START" });

        try {
            const requestData = {
                fromDate,
                toDate,
                search,
            };

            const response = await IpdRegistrationReportApi(requestData);

            if (response.success) {
                showToast("success", response.message);

                const formattedData = response.data.map((item, index) => ({
                    index: index + 1,
                    id: item._id,
                    type: item.docType || "",
                    ipdNo: item.ipdNo || "",
                    uhidNo: item.uhidNo || "",
                    ipdDirect: item.ipdDirect || "",
                    rawDate: item.date || "",
                    date: item.date
                        ? new Date(item.date).toLocaleDateString("en-IN")
                        : "",
                    patientName:
                        (item.patientNamePrefix ? item.patientNamePrefix + " " : "") +
                        (item.patientName || ""),
                    sex: item.sex || "",
                    ageValue: item.age || 0,
                    age: item.age ? `${item.age} ${item.ageSuffix || ""}` : "",
                    mobileNo: item.mobileNo || "",
                    address: item.address || "",
                    room: item.roomId?.roomNo || "",
                    consultantDoctor: item.consultantDoctor || "",
                    department: item.departmentId?.name || "",
                    foreiger: item.foreiger || "No",
                    drugSensitivity: item.drugSensitivity || "No",
                    familyHistory: item.familyHistory || "No",
                    remarks: item.remarks || "No",
                    advance: item.advance || 0,
                    paymentMode: item.paymentMode || "",
                    debitAccount: item.debitAccount || "",
                    idType: item.idType || "",
                    idNo: item.idNo || "",
                    attendent: item.attendent || "",
                    broughtBy: item.broughtBy || "",
                    createdBy: item.createdBy?.name || "",
                    createdAt: item.createdAt
                        ? new Date(item.createdAt).toLocaleDateString("en-IN")
                        : "",
                    updatedBy: item.updatedBy?.name || "",
                    updatedAt: item.updatedAt
                        ? new Date(item.updatedAt).toLocaleDateString("en-IN")
                        : "",
                }));

                setFilteredData(formattedData);
            } else {
                showToast("error", response.message);
            }
        } catch (error) {
            showToast("error", error.message);
        } finally {
            dispatch({ type: "LOADER_STOP" });
        }
    };

    const headers = [
        { label: "S.No", key: "index" },
        { label: "IPD No", key: "ipdNo" },
        { label: "UHID No", key: "uhidNo" },
        { label: "IPD Direct", key: "ipdDirect" },
        { label: "Date", key: "date" },
        { label: "Patient Name", key: "patientName" },
        { label: "Sex", key: "sex" },
        { label: "Age", key: "age" },
        { label: "Mobile No", key: "mobileNo" },
        { label: "Address", key: "address" },
        { label: "Room", key: "room" },
        { label: "Consultant Doctor", key: "consultantDoctor" },
        { label: "Department", key: "department" },
        { label: "Foreigner", key: "foreiger" },
        { label: "Drug Sensitivity", key: "drugSensitivity" },
        { label: "Family History", key: "familyHistory" },
        { label: "Remarks", key: "remarks" },
        { label: "Advance", key: "advance" },
        { label: "Payment Mode", key: "paymentMode" },
        { label: "Debit Account", key: "debitAccount" },
        { label: "ID Type", key: "idType" },
        { label: "ID No", key: "idNo" },
        { label: "Attendent", key: "attendent" },
        { label: "Brought By", key: "broughtBy" },
        { label: "Created By", key: "createdBy" },
        { label: "Created Date", key: "createdAt" },
        { label: "Updated By", key: "updatedBy" },
        { label: "Updated Date", key: "updatedAt" },
    ];

    const columns = headers.map((h) => ({
        name: h.label,
        selector: (row) => row[h.key],
        sortable: true,
        width: "160px",
    }));

    const exportToExcel = () => {
        const sheetData = filteredData.map((row) => {
            let obj = {};
            headers.forEach((h) => (obj[h.label] = row[h.key]));
            return obj;
        });

        const worksheet = XLSX.utils.json_to_sheet(sheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "IPD Report");
        XLSX.writeFile(workbook, "IPD_Report.xlsx");
    };

    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text("IPD Registration Report", 14, 10);

        const tableColumn = headers.map((h) => h.label);
        const tableRows = filteredData.map((row) => headers.map((h) => row[h.key]));

        autoTable(doc, { head: [tableColumn], body: tableRows, startY: 20 });
        doc.save("IPD_Report.pdf");
    };

    const resetFilters = () => {
        setFromDate("");
        setToDate("");
        setSearch("");
        setFilteredData([]);
    };

    return (
        <Fragment>
            <Breadcrumb parent="IPD" title="IPD Registration Report" />

            <div className="container-fluid">
                <div className="row">
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-header">
                                <h5>IPD Registration Report</h5>

                                <div className="row mt-3">
                                    <div className="col-sm-2">
                                        <label>From Date</label>
                                        <input
                                            className="form-control"
                                            type="date"
                                            value={fromDate}
                                            onChange={(e) => setFromDate(e.target.value)}
                                        />
                                    </div>

                                    <div className="col-sm-2">
                                        <label>To Date</label>
                                        <input
                                            className="form-control"
                                            type="date"
                                            value={toDate}
                                            onChange={(e) => setToDate(e.target.value)}
                                        />
                                    </div>

                                    <div className="col-sm-3">
                                        <label>Search</label>
                                        <input
                                            className="form-control"
                                            type="text"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            placeholder="Search..."
                                        />
                                    </div>

                                    <div className="col-sm mt-4">
                                        <button
                                            className="btn btn-primary me-2"
                                            onClick={fetchData}
                                        >
                                            Search
                                        </button>
                                        <button
                                            className="btn btn-warning"
                                            onClick={resetFilters}
                                        >
                                            Reset
                                        </button>
                                    </div>

                                    <div className="col-sm mt-4 text-end">
                                        <button
                                            className="btn btn-success me-2"
                                            onClick={exportToExcel}
                                        >
                                            Excel
                                        </button>
                                        <button
                                            className="btn btn-danger"
                                            onClick={exportToPDF}
                                        >
                                            PDF
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="card-body">
                                <DataTable
                                    columns={columns}
                                    data={filteredData}
                                    pagination
                                    highlightOnHover
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default IpdRegistrationReport;
